import React from 'react';
import { graphql, Script, useStaticQuery } from 'gatsby';
import PropTypes from 'prop-types';

const SEO = ({
  title,
  description,
  image,
  imageTwitter,
  imageAltText,
  location,
  productName,
  productRatingValue,
  productReviewCount,
  children,
}) => {
  const data = useStaticQuery(graphql`
    query {
      site {
        siteMetadata {
          fallbackTitle: title
          fallbackDescription: description
          siteUrl
          fallbackImage: image
          fallbackImageTwitter: imageTwitter
          fallbackImageAltText: imageAltText
        }
      }
    }
  `);

  const { fallbackTitle, fallbackDescription, siteUrl, fallbackImage, fallbackImageTwitter, fallbackImageAltText } =
    data.site.siteMetadata;

  const seo = {
    title: title ? `${title} - Piaggio Fast Forward` : fallbackTitle,
    description: description || fallbackDescription,
    image: image || `${siteUrl}/${fallbackImage}`,
    url: `${siteUrl}${location?.pathname || ''}`,
    imageTwitter: imageTwitter || `${siteUrl}/${fallbackImageTwitter}`,
    imageAltText: imageAltText || fallbackImageAltText,
  };

  // Schema for Google Rich Results
  const ldJson = {
    '@context': 'https://schema.org/',
    '@type': 'Product',
    name: productName,
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: productRatingValue,
      reviewCount: productReviewCount,
    },
  };

  return (
    <>
      <html lang="en" />
      <title>{seo.title}</title>

      <link rel="canonical" href={seo.url} />
      {/* Fav Icons */}
      <link rel="shortcut icon" href="/favicon.ico" />

      {/* Meta Tags */}
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      <meta charSet="utf-8" />
      <meta name="description" content={seo.description} />
      {/* TODO: update with new verification tag */}
      <meta name="google-site-verification" content="JlFLQ8nYkiGJ1IZxMa_ioaLssGyfO97BOFUlW8ipW4c" />
      {/* Facebook Business Manager domain verification */}
      <meta name="facebook-domain-verification" content="k3b42g1h51y2hte4pomgn4hg9sd576" />
      <meta name="referrer" content="strict-origin-when-cross-origin" />

      {/* Open Graph */}
      <meta property="og:title" content={seo.title} key="og:title" />
      <meta property="og:type" content="website" />
      <meta property="og:image" content={seo.image} />
      {location && <meta property="og:url" content={location.href} />}
      <meta property="og:description" content={seo.description} key="ogdesc" />
      <meta property="og:site_name" content={fallbackTitle} key="ogsitename" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@piaggiofastfwd" />
      <meta name="twitter:title" content={seo.title} />
      <meta name="twitter:image" content={seo.imageTwitter} />
      <meta name="twitter:description" content={seo.description} />
      <meta name="twitter:image:alt" content={seo.imageAltText} />

      {children}

      {/* Google Star Reviews */}
      {productReviewCount > 0 && <Script type="application/ld+json">{JSON.stringify(ldJson)}</Script>}
    </>
  );
};

SEO.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
  image: PropTypes.string,
  imageTwitter: PropTypes.string,
  imageAltText: PropTypes.string,
  location: PropTypes.any,
  productName: PropTypes.string,
  productRatingValue: PropTypes.number,
  productReviewCount: PropTypes.number,
  children: PropTypes.node,
};

SEO.defaultProps = {
  productRatingValue: 5,
  productReviewCount: 0,
};

export default SEO;
